import qs from 'qs';
import GatewayAxiosClient from '../GatewayAxiosClient';
import type {
  FulfillmentEngineFulfillmentCenter,
  FulfillmentEngineGetAllFulfillmentCentersV1Params,
  FulfillmentEngineGetFulfillmentCenterV1Params,
  FulfillmentEngineGetPlanV1Params,
  FulfillmentEngineFEOrder,
  FulfillmentEnginePlan,
  FulfillmentEnginePlanSummary,
  FulfillmentEngineSearchPlansV1Params,
  FulfillmentEngineFulfillmentCenterCreate,
  FulfillmentEngineFulfillmentCenterUpdate,
  FulfillmentEngineGlobalConfigCreate,
  FulfillmentEngineGetAllGlobalConfigsV1Params,
  FulfillmentEngineGlobalConfig,
  FulfillmentEngineGlobalConfigUpdate,
  FulfillmentEnginePlanDetails,
} from '@shipium-corp/shipium-ts-types';
import type { FulfillmentEngineGetOrderParams } from './types';

export class FulfillmentEngineService {
  // get all fulfillment center configs
  public async fetchAllFulfillmentCenterConfigs(
    payload: FulfillmentEngineGetAllFulfillmentCentersV1Params,
  ): Promise<FulfillmentEngineFulfillmentCenter[]> {
    const res = await GatewayAxiosClient.get('/fulfillment-engine-proxy/api/v1/fe/fulfillmentCenters', {
      params: payload,
    });
    return res.data;
  }

  // get fulfillment center configs
  public async fetchFulfillmentCenterConfigs(
    payload: FulfillmentEngineGetFulfillmentCenterV1Params,
  ): Promise<FulfillmentEngineFulfillmentCenter[]> {
    const res = await GatewayAxiosClient.get(
      `/fulfillment-engine-proxy/api/v1/fe/fulfillmentCenters/${payload.fulfillmentCenterId}`,
      {
        params: payload,
      },
    );
    return res.data;
  }

  //create a new fulfillment center config
  public async createFulfillmentCenterConfig(
    payload: FulfillmentEngineFulfillmentCenterCreate,
  ): Promise<FulfillmentEngineFulfillmentCenter> {
    const res = await GatewayAxiosClient.post(`/fulfillment-engine-proxy/api/v1/fe/fulfillmentCenters`, payload);
    return res.data;
  }

  public async updateFulfillmentCenterConfig(
    fcId: string,
    payload: FulfillmentEngineFulfillmentCenterUpdate,
  ): Promise<FulfillmentEngineFulfillmentCenter> {
    const res = await GatewayAxiosClient.put(`/fulfillment-engine-proxy/api/v1/fe/fulfillmentCenters/${fcId}`, payload);
    return res.data;
  }

  public async fetchGlobalConfigs(
    payload: FulfillmentEngineGetAllGlobalConfigsV1Params,
  ): Promise<FulfillmentEngineGlobalConfig[]> {
    const res = await GatewayAxiosClient.get('/fulfillment-engine-proxy/api/v1/fe/globalConfigs', {
      params: payload,
    });
    return res.data;
  }

  public async createGlobalConfig(
    payload: FulfillmentEngineGlobalConfigCreate,
  ): Promise<FulfillmentEngineGlobalConfig> {
    const res = await GatewayAxiosClient.post('/fulfillment-engine-proxy/api/v1/fe/globalConfigs', payload);
    return res.data;
  }

  public async updateGlobalConfig(
    globalConfigId: string,
    payload: FulfillmentEngineGlobalConfigUpdate,
  ): Promise<FulfillmentEngineGlobalConfig> {
    const res = await GatewayAxiosClient.put(
      `/fulfillment-engine-proxy/api/v1/fe/globalConfigs/${globalConfigId}`,
      payload,
    );
    return res.data;
  }

  public async searchPlansAndOrders(
    payload: FulfillmentEngineSearchPlansV1Params,
  ): Promise<FulfillmentEnginePlanSummary[]> {
    const res = await GatewayAxiosClient.get('/fulfillment-engine-proxy/api/v1/fe/internal/mashups/plansAndOrders', {
      params: payload,
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    });
    return res.data;
  }

  public async fetchPlan(payload: FulfillmentEngineGetPlanV1Params): Promise<FulfillmentEnginePlan> {
    const res = await GatewayAxiosClient.get(`/fulfillment-engine-proxy/api/v1/fe/plans/${payload.planId}`, {
      params: payload,
    });
    return res.data;
  }

  public async fetchPlanDetails(payload: FulfillmentEngineGetPlanV1Params): Promise<FulfillmentEnginePlanDetails> {
    const res = await GatewayAxiosClient.get(`/fulfillment-engine-proxy/api/v1/fe/plans/${payload.planId}/details`, {
      params: payload,
    });
    return res.data;
  }

  public async fetchOrders(payload: FulfillmentEngineGetPlanV1Params): Promise<FulfillmentEngineFEOrder[]> {
    const res = await GatewayAxiosClient.get(`/fulfillment-engine-proxy/api/v1/fe/plans/${payload.planId}/orders`, {
      params: payload,
    });
    return res.data;
  }

  public async fetchOrder(payload: FulfillmentEngineGetOrderParams): Promise<FulfillmentEngineFEOrder> {
    const res = await GatewayAxiosClient.get(
      `/fulfillment-engine-proxy/api/v1/fe/plans/${payload.planId}/orders/${payload.partnerOrderId}`,
      {
        params: payload,
      },
    );
    return res.data;
  }
}
